import { useQuery } from '@tanstack/react-query';
import { AnimatePresence } from 'framer-motion';
import { DateTime } from 'luxon';
import { FC, useEffect, useState } from 'react';
import styled from 'styled-components';

import Button from '@/components/Button';
import { PageHeading, SrOnly } from '@/components/Layout/Parts';
import SearchBar from '@/components/SearchBar';
import { SortableTh, Table, TableContent, Td, Th } from '@/components/Table';
import Text from '@/components/Text';
import useNetwork from '@/hooks/dq/useNetwork';
import useDebounce from '@/hooks/useDebounce';
import useSortState from '@/hooks/useSortState';
import { DQScheduledActionsPage } from '@/types/resources';
import { api } from '@/utils/api';
import { getDQActionLabel } from '@/utils/dq';
import { SortDirection } from '@/utils/enums';
import { set } from '@/utils/storage';

import NetworkSwitch from '../EditorialDistributionQueue/components/NetworkSwitch';
import LogModal from './components/LogModal';
import NewActionForm from './components/NewActionForm';

const Header = styled.div`
  display: flex;
  margin: 24px 0 12px;
  align-items: flex-end;
  justify-content: space-between;
`;

const Heading = styled(Text)`
  display: flex;
  align-items: center;
  text-transform: uppercase;
`;

const Badge = styled(Text)`
  display: flex;
  align-items: center;
  height: 18px;
  padding: 0 9px;
  border-radius: 9px;
  background: ${({ theme }) => theme.colors.gray};
  margin-right: 12px;
`;

const PageName = styled.div`
  display: inline-flex;
  align-items: center;
`;

const PageThumb = styled.img`
  width: 16px;
  height: 16px;
  border-radius: 100%;
  object-fit: cover;
  border: 1px solid ${({ theme }) => theme.colors.lightGray};
  flex-shrink: 0;
  margin-right: 8px;
`;

const StyledTable = styled(Table)`
  td,
  th {
    font-weight: 700;

    &:first-child {
      width: 254px;
    }
    &:nth-child(6) {
      width: 90px;
    }
    &:last-child {
      width: 120px;
    }
  }
`;

const ActionLogButton = styled.button.attrs({ type: 'button' })`
  width: 32px;
  height: 32px;
  border: 1px solid ${({ theme }) => theme.colors.lightGray};
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.black};
  transition: 0.15s ease-out background-color;

  &:hover {
    background: ${({ theme }) => theme.colors.grayscaleLight};
  }
`;

const StyledNetworkSwitch = styled(NetworkSwitch)`
  margin-right: 0;
`;

const DQScheduledActions: FC = () => {
  const [network, networkLabel] = useNetwork();

  const [search, setSearch] = useState('');

  const debouncedSearch = useDebounce(search, 500);

  const { sort, setSort, sortQueryParamObj } = useSortState({
    property: 'name',
    direction: SortDirection.ASC,
  });

  const [activePage, setActivePage] = useState<DQScheduledActionsPage | null>(
    null,
  );

  const [newActionPage, setNewActionPage] =
    useState<DQScheduledActionsPage | null>(null);

  const {
    data: pages,
    isLoading,
    isError,
  } = useQuery(
    [
      'distribution_queue_pages_and_actions',
      {
        network,
        sort,
        ...(debouncedSearch.length > 2 && {
          search: debouncedSearch,
        }),
      },
    ],
    () =>
      api.get<never, DQScheduledActionsPage[]>(
        '/v1/editorial/queue/pages-and-actions',
        {
          params: {
            network,
            ...(!!sortQueryParamObj && sortQueryParamObj),
            ...(debouncedSearch.length > 2 && {
              'filter[search]': debouncedSearch,
            }),
          },
        },
      ),
  );

  useEffect(() => {
    set('dq_scheduled_actions_network', networkLabel);
  }, [networkLabel]);

  return (
    <>
      <Text $type="caption" $color="gray">
        EDITORIAL
      </Text>
      <PageHeading>Scheduled Actions</PageHeading>
      <SearchBar
        placeholder="Search pages"
        value={search}
        setValue={setSearch}
      />
      <Header>
        <Heading $color="gray" $type="smallHeadline" as="div">
          <Badge as="div" $type="extraSmall" $color="white">
            {pages?.length ?? '-'}
          </Badge>
          {networkLabel} · page list & actions
        </Heading>
        <StyledNetworkSwitch />
      </Header>
      <StyledTable $fixed>
        <thead>
          <tr>
            <SortableTh
              title="Page Name"
              sort={sort}
              setSort={setSort}
              property="name"
            />
            <SortableTh
              title="Actions Count"
              sort={sort}
              setSort={setSort}
              property="actionsCount"
            />
            <Th>Last Action Added</Th>
            <SortableTh
              title="Last Action Date"
              sort={sort}
              setSort={setSort}
              property="lastActionDate"
            />
            <Th>Last Action Owner</Th>
            <Th>Action log</Th>
            <Th>
              <SrOnly>Actions</SrOnly>
            </Th>
          </tr>
        </thead>
        <tbody>
          <TableContent
            colSpan={7}
            isLoading={isLoading}
            isError={isError}
            hasData={!!pages?.length}
          >
            {pages?.map((page) => (
              <tr key={page.id}>
                <Td>
                  <PageName>
                    {!!page.thumbnail && (
                      <PageThumb loading="lazy" src={page.thumbnail} alt="" />
                    )}
                    {page.name}
                  </PageName>
                </Td>
                <Td>{page.actionsCount}</Td>
                <Td>{getDQActionLabel(page.lastActionType)}</Td>
                <Td>
                  {page.lastActionDate ? (
                    <>
                      {DateTime.fromISO(page.lastActionDate).toFormat(
                        'dd/LL/yyyy',
                      )}
                      <br />
                      <Text as="span" $color="gray" $type="extraSmall">
                        {DateTime.fromISO(page.lastActionDate).toFormat(
                          'HH:mm',
                        )}
                      </Text>
                    </>
                  ) : (
                    '-'
                  )}
                </Td>
                <Td>{page.lastActionOwner ?? '-'}</Td>
                <Td>
                  <ActionLogButton onClick={() => setActivePage(page)}>
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M7.99777 14.8569C11.7849 14.8569 14.8549 11.7868 14.8549 7.99972C14.8549 4.21263 11.7849 1.14258 7.99777 1.14258C4.21067 1.14258 1.14062 4.21263 1.14062 7.99972C1.14062 11.7868 4.21067 14.8569 7.99777 14.8569Z"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M7.42578 7.42969H7.99721V11.4297H8.56864"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M7.99777 5.71429C8.47115 5.71429 8.85491 5.33053 8.85491 4.85714C8.85491 4.38376 8.47115 4 7.99777 4C7.52438 4 7.14062 4.38376 7.14062 4.85714C7.14062 5.33053 7.52438 5.71429 7.99777 5.71429Z"
                        fill="currentColor"
                      />
                    </svg>
                  </ActionLogButton>
                </Td>
                <Td>
                  <Button
                    color="colorized"
                    small
                    onClick={() => setNewActionPage(page)}
                  >
                    Add action
                  </Button>
                </Td>
              </tr>
            ))}
          </TableContent>
        </tbody>
      </StyledTable>
      <AnimatePresence>
        {!!newActionPage && (
          <NewActionForm
            onClose={() => setNewActionPage(null)}
            page={newActionPage}
          />
        )}
        {!!activePage && (
          <LogModal onClose={() => setActivePage(null)} page={activePage} />
        )}
      </AnimatePresence>
    </>
  );
};

export default DQScheduledActions;
